import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { websiteHost } from "@/components/record/recordFormat";
import { companyAddressesKey } from "@/hooks/useCompanyAddresses";
import type { CompanyData } from "../companyTypes";

export type ProspectEnrichPhase = "idle" | "website" | "apollo" | "review" | "done";

/** Apollo plus AI research, a two-column review, then apply. Names match the code these bodies came from. */
export function useProspectEnrichment(company: CompanyData) {
  const queryClient = useQueryClient();
  const [enrichPhase, setEnrichPhase] = useState<ProspectEnrichPhase>("idle");
  const [enrichResult, setEnrichResult] = useState<any>(null);
  const [enrichApplying, setEnrichApplying] = useState(false);
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({});
  const id = company.id;
  const domain = websiteHost(company.website) || company.enrichmentData?.domain;

  const runProspectEnrich = async () => {
    if (!domain) { toast.error("No website domain to enrich from"); return; }
    setEnrichPhase("apollo");
    try {
      const res = await fetch(`/api/companies/${id}/enrich-combined`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json();
      setEnrichResult(data);
      if (data.apollo || data.ai) {
        const a = data.apollo || {};
        const ai = data.ai || {};
        const cur = data.current || {};
        const selections: Record<string, boolean> = {};
        // Apollo fields
        if (a.industry && !cur.industry) selections.apolloIndustry = true;
        if (a.headquarters && !cur.headquarters) selections.apolloHq = true;
        if (a.employeeCount && !cur.employeeCount) selections.apolloEmployees = true;
        if (a.revenueRange && !cur.revenueRange) selections.apolloRevenue = true;
        if (a.linkedinUrl && !cur.linkedinUrl) selections.apolloLinkedin = true;
        if (a.companyAddress && !cur.hasCompanyAddress) selections.apolloAddress = true;
        if (a.phone) selections.apolloPhone = true;
        if (a.apolloId) selections.apolloId = true;
        if (a.foundedYear) selections.apolloFounded = true;
        // AI fields
        if (ai.companyDescription) selections.aiDescription = !cur.description;
        if (ai.whyGoodProspect) selections.aiThesis = true;
        if (ai.estimatedDealValue?.estimate) selections.aiDealValue = true;
        if (ai.ownerName) selections.aiOwner = true;
        if (ai.estimatedRevenue && !cur.revenueRange && !a.revenueRange) selections.aiRevenue = true;
        if (ai.estimatedEmployees && !cur.employeeCount && !a.employeeCount) selections.aiEmployees = true;
        setSelectedFields(selections);
        setEnrichPhase("review");
      } else {
        setEnrichPhase("done");
        toast.error("No data found");
      }
    } catch { setEnrichPhase("done"); toast.error("Enrichment failed"); }
  };

  const applyProspectEnrich = async () => {
    if (!enrichResult) return;
    setEnrichApplying(true);
    try {
      const a = enrichResult.apollo || {};
      const ai = enrichResult.ai || {};
      const sel = selectedFields;
      const fields: Record<string, any> = {};

      // Apollo fields → company columns
      if (sel.apolloIndustry) fields.industry = a.industry;
      if (sel.apolloHq) fields.headquarters = a.headquarters;
      if (sel.apolloEmployees) fields.employeeCount = String(a.employeeCount || "");
      if (sel.apolloRevenue) fields.revenueRange = a.revenueRange;
      if (sel.apolloLinkedin) fields.linkedinUrl = a.linkedinUrl;
      if (sel.apolloAddress && a.companyAddress) fields.companyAddress = a.companyAddress;
      if (sel.apolloPhone) fields.phone = a.phone;
      if (sel.apolloId) fields.apolloId = a.apolloId;
      if (sel.apolloFounded) fields.foundedYear = a.foundedYear;

      // AI fields → company columns
      if (sel.aiDescription && ai.companyDescription) fields.description = ai.companyDescription;
      if (sel.aiRevenue && ai.estimatedRevenue) fields.revenueRange = ai.estimatedRevenue;
      if (sel.aiEmployees && ai.estimatedEmployees) fields.employeeCount = String(ai.estimatedEmployees);

      // Metadata for enrichment_data JSONB
      const body: Record<string, any> = {
        fields,
        domain: domain || undefined,
        apolloId: a.apolloId || undefined,
        foundedYear: a.foundedYear || undefined,
        phone: a.phone || undefined,
        linkedinUrl: a.linkedinUrl || undefined,
      };
      if (sel.aiThesis && ai.whyGoodProspect) body.prospectThesis = ai.whyGoodProspect;
      if (ai.products?.length) body.products = ai.products;
      if (sel.aiDealValue && ai.estimatedDealValue?.estimate) body.estimatedDealValue = ai.estimatedDealValue.estimate;

      const res = await fetch(`/api/companies/${id}/apply-enrichment`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to apply enrichment");
        setEnrichApplying(false);
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["company", id] });
      queryClient.invalidateQueries({ queryKey: companyAddressesKey(id) });
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      toast.success("Enrichment applied");
      setEnrichPhase("done");
    } catch (err: any) {
      console.error("[Apply Error]", err);
      toast.error("Failed to apply: " + (err.message || "Unknown error"));
    }
    setEnrichApplying(false);
  };

  return {
    phase: enrichPhase, setPhase: setEnrichPhase, result: enrichResult, applying: enrichApplying,
    selected: selectedFields, setSelected: setSelectedFields, run: runProspectEnrich, apply: applyProspectEnrich,
  };
}

export type ProspectEnrichment = ReturnType<typeof useProspectEnrichment>;
